import React, { useMemo, useState } from 'react';
import {
  ANALYTICS_COLORS,
  ANALYTICS_GRANULARITY_OPTIONS,
  ANALYTICS_MESSAGES,
  CHART_STATE_EXTRA_HEIGHT_PX,
  GRANULARITY_LABELS,
  TIMELINE_CHART_CONFIG,
  TIMELINE_CHART_HEIGHT,
} from '../../../constants/analytics.constants';
import { AnalyticsGranularity, TimelinePoint } from '../../../types/analytics.types';
import {
  buildTimelineGeometry,
  hasAnyTimelineActivity,
} from '../../../utils/analytics-chart.utils';
import {
  formatAbsoluteDate,
  formatBucketLabel,
  sumSeries,
} from '../../../utils/analytics.utils';

interface AnalyticsTimelineChartProps {
  points: TimelinePoint[];
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
  isLoading: boolean;
}

const MIN_LABEL_STRIDE = 1;
const STATE_CONTAINER_HEIGHT_PX = TIMELINE_CHART_HEIGHT + CHART_STATE_EXTRA_HEIGHT_PX;
const EMPTY_POINT_COUNT = 0;
const BASELINE_TICK_VALUE = 0;
const ZERO_COUNT = 0;

const LoadingState: React.FC = () => (
  <div
    className="flex items-center justify-center"
    style={{ height: STATE_CONTAINER_HEIGHT_PX }}
  >
    <div className="flex items-center gap-3 text-slate-400">
      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
      <span className="text-xs">Loading timeline…</span>
    </div>
  </div>
);

const EmptyState: React.FC = () => (
  <div
    className="flex items-center justify-center"
    style={{ height: STATE_CONTAINER_HEIGHT_PX }}
  >
    <span className="text-xs text-slate-400">{ANALYTICS_MESSAGES.EMPTY_TIMELINE}</span>
  </div>
);

interface BucketToggleProps {
  granularity: AnalyticsGranularity;
  onGranularityChange: (granularity: AnalyticsGranularity) => void;
  isLoading: boolean;
}

const BucketToggle: React.FC<BucketToggleProps> = ({
  granularity,
  onGranularityChange,
  isLoading,
}) => (
  <div className="inline-flex rounded-lg bg-slate-50 border border-slate-200 p-0.5">
    {ANALYTICS_GRANULARITY_OPTIONS.map((opt) => {
      const isActive = granularity === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => onGranularityChange(opt.value)}
          disabled={isLoading}
          className={`px-2.5 py-1 text-[11px] font-semibold rounded-md transition-colors ${
            isActive
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-800'
          } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

interface LegendToggleProps {
  color: string;
  label: string;
  isActive: boolean;
  onToggle: () => void;
}

const LegendToggle: React.FC<LegendToggleProps> = ({ color, label, isActive, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-pressed={isActive}
    className={`flex items-center gap-1.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-slate-50 ${
      isActive ? '' : 'opacity-40'
    }`}
  >
    <span
      className="h-2.5 w-2.5 rounded-sm"
      style={{ backgroundColor: color, opacity: isActive ? 1 : 0.5 }}
    />
    <span className="text-slate-600 font-medium tabular-nums">{label}</span>
  </button>
);

const TimelineTooltip: React.FC<{ point: TimelinePoint }> = ({ point }) => (
  <div className="absolute top-3 right-3 rounded-lg border border-slate-200 bg-white/95 backdrop-blur shadow-md px-3 py-2 text-xs pointer-events-none">
    <div className="font-semibold text-slate-900 mb-1">{formatAbsoluteDate(point.bucket)}</div>
    <div className="flex items-center gap-3 tabular-nums">
      <span className="flex items-center gap-1.5 text-teal-700">
        <span className="h-2 w-2 rounded-full bg-teal-600" />
        Created <span className="font-semibold">{point.created}</span>
      </span>
      <span className="flex items-center gap-1.5 text-amber-700">
        <span className="h-2 w-2 rounded-full bg-amber-500" />
        Updated <span className="font-semibold">{point.updated}</span>
      </span>
    </div>
  </div>
);

interface SeriesVisibility {
  created: boolean;
  updated: boolean;
}

const INITIAL_SERIES_VISIBILITY: SeriesVisibility = { created: true, updated: true };

export const AnalyticsTimelineChart: React.FC<AnalyticsTimelineChartProps> = ({
  points,
  granularity,
  onGranularityChange,
  isLoading,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [series, setSeries] = useState<SeriesVisibility>(INITIAL_SERIES_VISIBILITY);

  const filteredPoints = useMemo(
    () =>
      points.map((p) => ({
        ...p,
        created: series.created ? p.created : 0,
        updated: series.updated ? p.updated : 0,
      })),
    [points, series],
  );

  const geometry = useMemo(() => buildTimelineGeometry(filteredPoints), [filteredPoints]);
  const hasActivity = useMemo(() => hasAnyTimelineActivity(filteredPoints), [filteredPoints]);
  const totalCreated = useMemo(() => sumSeries(points, 'created'), [points]);
  const totalUpdated = useMemo(() => sumSeries(points, 'updated'), [points]);

  const toggleSeries = (key: keyof SeriesVisibility): void => {
    setSeries((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (!next.created && !next.updated) return prev;
      return next;
    });
  };

  const labelStride = Math.max(
    MIN_LABEL_STRIDE,
    Math.ceil(points.length / TIMELINE_CHART_CONFIG.MAX_X_AXIS_LABELS),
  );

  const showLoading = isLoading && points.length === EMPTY_POINT_COUNT;
  const showEmpty = !isLoading && (points.length === EMPTY_POINT_COUNT || !hasActivity);
  const showChart = !showLoading && !showEmpty;

  const { padding, innerWidth, innerHeight, barGroupWidth, barWidth, yMax, yTicks } = geometry;
  const scaleY = (value: number): number => (value / yMax) * innerHeight;
  const lastIndex = points.length - 1;
  const granularityLabel = GRANULARITY_LABELS[granularity];

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm p-6">
      <div className="flex flex-col gap-3 mb-4 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-slate-900">Activity over time</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Created + updated CRs per {granularityLabel.singular}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <BucketToggle
            granularity={granularity}
            onGranularityChange={onGranularityChange}
            isLoading={isLoading}
          />
          {showChart && (
            <div className="flex items-center gap-1">
              <LegendToggle
                color={ANALYTICS_COLORS.CREATED}
                label={`Created · ${totalCreated.toLocaleString()}`}
                isActive={series.created}
                onToggle={() => toggleSeries('created')}
              />
              <LegendToggle
                color={ANALYTICS_COLORS.UPDATED}
                label={`Updated · ${totalUpdated.toLocaleString()}`}
                isActive={series.updated}
                onToggle={() => toggleSeries('updated')}
              />
            </div>
          )}
        </div>
      </div>

      {showLoading && <LoadingState />}
      {showEmpty && <EmptyState />}
      {showChart && (
        <div className="relative">
        <svg
          viewBox={`0 0 ${TIMELINE_CHART_CONFIG.WIDTH} ${TIMELINE_CHART_HEIGHT}`}
          className="w-full h-auto"
          preserveAspectRatio="none"
          role="img"
          aria-label="CR activity timeline"
        >
          {yTicks.map((tick) => {
            const y = padding.top + innerHeight - scaleY(tick);
            return (
              <g key={tick}>
                <line
                  x1={padding.left}
                  x2={padding.left + innerWidth}
                  y1={y}
                  y2={y}
                  stroke={ANALYTICS_COLORS.GRID}
                  strokeDasharray={tick === BASELINE_TICK_VALUE ? undefined : TIMELINE_CHART_CONFIG.GRID_DASH_NON_BASELINE}
                  strokeWidth={TIMELINE_CHART_CONFIG.GRID_STROKE_WIDTH}
                />
                <text
                  x={padding.left - TIMELINE_CHART_CONFIG.Y_LABEL_OFFSET_X}
                  y={y + TIMELINE_CHART_CONFIG.Y_LABEL_OFFSET_Y}
                  textAnchor="end"
                  fontSize={TIMELINE_CHART_CONFIG.AXIS_LABEL_FONT_SIZE}
                  fill={ANALYTICS_COLORS.AXIS}
                >
                  {tick}
                </text>
              </g>
            );
          })}

          {filteredPoints.map((p, i) => {
            const groupX = padding.left + i * barGroupWidth + (barGroupWidth - barWidth) / 2;
            const updatedH = scaleY(p.updated);
            const createdH = scaleY(p.created);
            const baseY = padding.top + innerHeight;
            const updatedY = baseY - updatedH;
            const createdY = updatedY - createdH;
            const isHovered = hoveredIndex === i;
            const showLabel = i % labelStride === 0 || i === lastIndex;
            const barOpacity = isHovered
              ? TIMELINE_CHART_CONFIG.BAR_OPACITY_HOVER
              : TIMELINE_CHART_CONFIG.BAR_OPACITY_DEFAULT;

            return (
              <g
                key={`${p.bucket}-${i}`}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <rect
                  x={padding.left + i * barGroupWidth}
                  y={padding.top}
                  width={barGroupWidth}
                  height={innerHeight}
                  fill="transparent"
                />
                {p.updated > ZERO_COUNT && (
                  <rect
                    x={groupX}
                    y={updatedY}
                    width={barWidth}
                    height={Math.max(TIMELINE_CHART_CONFIG.MIN_BAR_HEIGHT_PX, updatedH)}
                    fill={ANALYTICS_COLORS.UPDATED}
                    opacity={barOpacity}
                    rx={TIMELINE_CHART_CONFIG.BAR_CORNER_RADIUS}
                  />
                )}
                {p.created > ZERO_COUNT && (
                  <rect
                    x={groupX}
                    y={createdY}
                    width={barWidth}
                    height={Math.max(TIMELINE_CHART_CONFIG.MIN_BAR_HEIGHT_PX, createdH)}
                    fill={ANALYTICS_COLORS.CREATED}
                    opacity={barOpacity}
                    rx={TIMELINE_CHART_CONFIG.BAR_CORNER_RADIUS}
                  />
                )}
                {isHovered && (
                  <line
                    x1={groupX + barWidth / 2}
                    x2={groupX + barWidth / 2}
                    y1={padding.top}
                    y2={padding.top + innerHeight}
                    stroke={ANALYTICS_COLORS.AXIS}
                    strokeWidth={TIMELINE_CHART_CONFIG.HOVER_LINE_STROKE_WIDTH}
                    strokeDasharray={TIMELINE_CHART_CONFIG.HOVER_LINE_DASH}
                    opacity={TIMELINE_CHART_CONFIG.HOVER_LINE_OPACITY}
                  />
                )}
                {showLabel && (
                  <text
                    x={groupX + barWidth / 2}
                    y={padding.top + innerHeight + TIMELINE_CHART_CONFIG.X_LABEL_OFFSET_Y}
                    textAnchor="middle"
                    fontSize={TIMELINE_CHART_CONFIG.AXIS_LABEL_FONT_SIZE}
                    fill={ANALYTICS_COLORS.AXIS}
                  >
                    {formatBucketLabel(p.bucket, granularity)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

          {hoveredIndex !== null && points[hoveredIndex] && (
            <TimelineTooltip point={points[hoveredIndex]} />
          )}
        </div>
      )}
    </div>
  );
};
